import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { FeatureGateService } from 'src/shared/services/feature-gate.service';

import { RentalUnitsService, RentalUnit, RentableSpace, OccupancyClassification } from 'src/shared/services/rental-units.service';

interface RoomForm {
  id: FormControl<number | null>;
  name: FormControl<string>;
}

// Common areas are a simpler add/remove list of names — no rent/cadence, they
// aren't leasable (CLAUDE.md: no template function calls, keep it minimal).
interface CommonAreaForm {
  id: FormControl<number | null>;
  name: FormControl<string>;
}

let nextTempId = -1; // negative temp ids for new rows, replaced by real ids on save

@Component({
  templateUrl: 'room-setup.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RoomSetupPage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private featureGate = inject(FeatureGateService);
  private unitsService = inject(RentalUnitsService);

  readonly windowService = inject(WindowService);

  readonly classificationItems = [
    { value: 'standard_tenancy', viewValue: 'Standard tenancy' },
    { value: 'rooming_house', viewValue: 'Rooming house' },
  ];

  isRightSideContent = true;
  unitId: number;
  unit: RentalUnit;
  isEditing = false;
  loaded = false;
  submitted = false;
  saving = false;
  errorMessage: string;

  classificationForm = this.fb.group({
    classification: this.fb.control<OccupancyClassification>('standard_tenancy', { nonNullable: true }),
  });
  rooms = this.fb.array<FormGroup<RoomForm>>([]);
  commonAreas = this.fb.array<FormGroup<CommonAreaForm>>([]);

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const params = await this.storage.retrieve('dialog-params');
    this.unitId = this.navCtrl.getParam('unitId') ?? params?.unitId;
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle('Room Setup');
    }
    await this.load();
    this.loaded = true;
    this.cdr.markForCheck();
  }

  async load(): Promise<void> {
    if (!this.unitId) {
      this.errorMessage = 'No unit selected.';
      return;
    }
    try {
      this.unit = await this.unitsService.getUnit(this.unitId);
      if (!this.unit) {
        this.errorMessage = 'Unit not found.';
        return;
      }
      this.isEditing = this.unitsService.isByRoom(this.unit);
      this.classificationForm.controls.classification.setValue(this.unit.occupancy_classification || 'standard_tenancy');
      if (this.isEditing) {
        this.prefillFromExistingSpaces(this.unit.spaces || []);
      } else {
        this.prefillFreshRooms(this.unit);
      }
    } catch {
      this.errorMessage = 'Could not load this unit.';
    }
  }

  private prefillFreshRooms(unit: RentalUnit): void {
    const roomCount = unit.bedrooms > 0 ? unit.bedrooms : 1;
    for (let i = 0; i < roomCount; i++) {
      this.rooms.push(this.buildRoomGroup({ id: null, name: `Room ${String.fromCharCode(65 + i)}` }));
    }
  }

  private prefillFromExistingSpaces(spaces: RentableSpace[]): void {
    for (const space of spaces) {
      if (space.type === 'room') {
        this.rooms.push(this.buildRoomGroup({ id: space.id, name: space.name }));
      } else {
        this.commonAreas.push(this.buildCommonAreaGroup(space.id, space.name));
      }
    }
  }

  private buildRoomGroup(value: { id: number | null; name: string }): FormGroup<RoomForm> {
    return this.fb.group<RoomForm>({
      id: this.fb.control<number | null>(value.id),
      name: this.fb.control(value.name, { validators: [Validators.required], nonNullable: true }),
    });
  }

  private buildCommonAreaGroup(id: number | null, name: string): FormGroup<CommonAreaForm> {
    return this.fb.group<CommonAreaForm>({
      id: this.fb.control<number | null>(id),
      name: this.fb.control(name, { validators: [Validators.required], nonNullable: true }),
    });
  }

  addRoom = () => {
    const nextLetter = String.fromCharCode(65 + this.rooms.length);
    this.rooms.push(this.buildRoomGroup({ id: null, name: `Room ${nextLetter}` }));
  };

  removeRoom(index: number) {
    this.rooms.removeAt(index);
  }

  addCommonArea = () => {
    this.commonAreas.push(this.buildCommonAreaGroup(null, ''));
  };

  removeCommonArea(index: number) {
    this.commonAreas.removeAt(index);
  }

  private buildSpacesPayload(): RentableSpace[] {
    const existingById = new Map((this.unit?.spaces || []).map(s => [s.id, s]));
    const roomSpaces: RentableSpace[] = this.rooms.controls.map(group => {
      const value = group.getRawValue();
      const existing = value.id !== null && value.id !== undefined ? existingById.get(value.id) : undefined;
      if (existing) {
        // Merge the name edit onto the existing space so occupancy fields
        // (tenant, lease, screening) AND pricing (rent, cadence, furnished —
        // set elsewhere, in listings/leases) survive an "edit rooms" save.
        return { ...existing, name: value.name };
      }
      // Brand-new rooms have no pricing yet; rent/cadence/furnished are left
      // undefined and set later from listings/leases, not here.
      return {
        id: nextTempId--,
        name: value.name,
        type: 'room',
        state: 'Vacant',
        days_vacant: 0,
      };
    });
    const commonSpaces: RentableSpace[] = this.commonAreas.controls.map(group => {
      const value = group.getRawValue();
      const existing = value.id !== null && value.id !== undefined ? existingById.get(value.id) : undefined;
      if (existing) {
        return { ...existing, name: value.name };
      }
      return { id: nextTempId--, name: value.name, type: 'common' };
    });
    return [...roomSpaces, ...commonSpaces];
  }

  save = async () => {
    this.submitted = true;
    if (this.rooms.invalid || this.commonAreas.invalid || this.rooms.length === 0) {
      return;
    }
    if (this.featureGate.gate()) { return; }
    this.saving = true;
    try {
      const spaces = this.buildSpacesPayload();
      if (this.isEditing) {
        await this.unitsService.updateSpaces(this.unitId, spaces);
        this.util.showSuccess('Rooms updated.');
      } else {
        await this.unitsService.convertToByRoom(this.unitId, {
          occupancy_classification: this.classificationForm.controls.classification.value,
          spaces,
        });
        this.util.showSuccess('Unit converted to by-the-room.');
      }
      this.returnToUnitDetail();
    } catch {
      this.errorMessage = 'Could not save room setup.';
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  };

  revertToEntire = async () => {
    if (this.featureGate.gate()) { return; }
    this.saving = true;
    try {
      const result = await this.unitsService.revertToEntire(this.unitId);
      if (!result.ok) {
        this.util.showError(result.error || 'Could not convert this unit back to an entire unit.');
        return;
      }
      this.util.showSuccess('Unit converted back to an entire unit.');
      this.returnToUnitDetail();
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  };

  // Matches the codebase's dual-nav "save then return to caller" convention
  // (e.g. add-edit-property-tax.ts goBack()). DUMMY_UNITS mutations are
  // in-place on the shared object, so the unit-detail page the user returns
  // to reflects the change without a separate reload flag.
  private returnToUnitDetail(): void {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.back();
    }
  }
}
